import React, { KeyboardEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { IconPhone } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { capitalizeFirstLetter } from 'src/utils';
import { ReactComponent as IconError } from 'src/assets/icons/toast-error.svg'
import { ReactComponent as IconSuccess } from 'src/assets/icons/toast-success.svg'
import { ErrorFallback } from 'src/utils/constants';
import useCleverTapEvents from 'src/events/useGetEvents';
import { MOBILE_EVENT } from 'src/events/constant';
import { clearAccountError, generateOTP, setAccountStatus } from './accountSlice';
import { ScreenWrapper } from '../../components';
import { useStyles } from './signup.styles';
import { allowOnlyNumber } from '../../utils/helpers';
import { Button, TextInput } from '../../components/atoms';

const regexNumCheck = /^[6-9]{1}[0-9]{9}$/;
const websiteUrl = process.env.REACT_APP_WEBSITE_URL;

export const Signup = (): JSX.Element => {
  const location = useLocation();
  const locState: any = location.state;
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const accountState = useSelector((state: any) => state.account);
  const { mobileNumber, authToken } = accountState;
  const [mobile, setMobile] = useState<string>(mobileNumber || '');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { identifyUserOnClevertap, TriggerEvent } = useCleverTapEvents();
  const isButtonDisabled = mobile.length < 10 || accountState.status === 'loading' || errorMessage.length > 0;

  useEffect(() => {
    if (authToken) {
      navigate(locState?.from?.pathname || '/', { replace: true });
    }
  }, [authToken]);

  useEffect(() => {
    if (accountState.status === 'success') {
      identifyUserOnClevertap(accountState.mobileNumber);
      notifications.show({
        color: 'green',
        message: 'Successfully sent OTP!',
        icon: <IconSuccess />,
      });
      navigate('/verify-otp');
      dispatch(setAccountStatus('idle'));
    } else if (accountState.status === 'error') {
      notifications.show({
        color: 'red',
        message: capitalizeFirstLetter(accountState.error.message) || ErrorFallback,
        icon: <IconError />,
      });
      dispatch(clearAccountError());
    }
  }, [accountState.status]);

  const handleMobileSubmit = async (): Promise<void> => {
    if (!isButtonDisabled || !isLoading) {
      setIsLoading(true);
      if (errorMessage) {
        setIsLoading(false);
      } else {
        dispatch(generateOTP({
          mobileNumber: mobile,
        }));
        TriggerEvent(MOBILE_EVENT.mobileSubmit, { });
      }
    }
  };

  const onNextClick = () => {
    handleMobileSubmit();
  };

  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const num = e.target.value;
    if (allowOnlyNumber(num)) {
      setMobile(num);
      if ((+num[0] < 6) || (num.length === 10 && !regexNumCheck.test(num))) {
        setErrorMessage('Enter valid Mobile Number');
      } else {
        setErrorMessage('');
      }
    }
  };

  const handleKeyPress = (event: KeyboardEvent): void => {
    if (event.key === 'Enter' && !isButtonDisabled) {
      handleMobileSubmit();
    }
  };

  return (
    <ScreenWrapper>
      <div className="csMainContainer">
        <div className={classes.contentContainer}>
          <h2 className="csHeading">Let&apos;s get started</h2>
          <p className="csDescription">Enter your mobile number.</p>
          <TextInput
            icon={<IconPhone stroke="1.2px" />}
            type="tel"
            name="mobile"
            id="mobile"
            inputMode="numeric"
            title="Enter your mobile number"
            autoComplete="off"
            autoFocus
            required
            onChange={handleMobileChange}
            onKeyDown={handleKeyPress}
            value={mobile}
            maxLength={10}
            error={errorMessage}
          />
          <p className={classes.inputDescription}>
            By submitting your contact number, you are agreeing to our
            {" "}
            <a
              onClick={() => TriggerEvent(MOBILE_EVENT.tAndC, {})}
              className={classes.tAndCLink}
              target="blank"
              href={`${websiteUrl}/terms-and-conditions/`}
            >
              Terms & Conditions
            </a>
          </p>
        </div>
        <div className="csFooter">
          <Button
            disabled={isButtonDisabled}
            onClick={onNextClick}
            loading={accountState.status === 'loading'}
          >
            Next
          </Button>
        </div>
      </div>
    </ScreenWrapper>
  );
};

export default Signup;
